import { render, html } from 'lit';
import '../elements/katapult-elements/katapult-button.js';
import '../elements/katapult-dialog-legacy/katapult-dialog-legacy.js';
export class DialogService {
  static baseZIndex = 999;

  static _records = [];

  static get pageElement() {
    return document.getElementById('pageElement');
  }

  static get backdropCount() {
    return this._records.filter((record) => record.backdrop).length;
  }

  static updateOverlay() {
    this.pageElement?.setOverlayDepth(this.backdropCount);
  }

  static register(dialog) {
    if (!this._records.find((record) => record.dialog == dialog)) {
      const record = {
        dialog,
        backdrop: dialog.backdrop
      };
      const index = this._records.push(record);
      this.updateOverlay();
      return { index, zIndex: index + this.baseZIndex };
    }
  }

  static unregister(dialog) {
    const index = this._records.findIndex((record) => record.dialog == dialog);
    if (index != -1) {
      const record = this._records[index];
      this._records.splice(index, 1);
      this.updateOverlay();
      return { index, zIndex: index + this.baseZIndex };
    }
  }

  // Create a generic dialog.
  static dialog(options = {}) {
    const slot = this.pageElement.serviceSlot('dialog');
    if (!slot) throw 'Failed to find dialog-service slot';
    // Create a dialog.
    const dialog = document.createElement('katapult-dialog-legacy');
    // Append it to the dom.
    slot.appendChild(dialog);
    try {
      // Set Attributes.
      for (const attr in options.attributes) dialog.setAttribute(attr, options.attributes[attr]);

      // Set Properties.
      for (const key in options.properties) dialog.set(key, options.properties[key]);

      // Render the template.
      render(options.template, dialog);

      // When the dialog has closed, remove it from the dom.
      dialog.addEventListener('closed', () => slot.removeChild(dialog));

      // Return the dialog and the open promise.
      return { dialog, promise: dialog.open() };
    } catch (err) {
      // If anything goes wrong, remove the dialog from the dom.
      slot.removeChild(dialog);
    }
  }

  static prompt(options = {}) {
    options.class ??= 'prompt';
    // Initialize options with default values.
    DialogService._initOptions(options);
    // Override template.
    const template = html`
      <span slot="title">${options.title}</span>
      <span slot="body" style="${options.bodyStyle}">${options.message}</span>
      <katapult-button slot="buttons" dialog-dismiss>Cancel</katapult-button>
      <div spacer slot="buttons"></div>
      <katapult-button icon="${options.button.icon}" slot="buttons" color="${options.button.color}" button-confirm
        >${options.button.text}</katapult-button
      >
    `;
    // Create Dialog.
    return DialogService.dialog({
      attributes: options.attributes,
      properties: options.properties,
      execute: options.execute,
      template
    });
  }

  static confirm(options = {}) {
    options.class ??= 'confirm';
    // Initialize options with default values.
    DialogService._initOptions(options);
    // Override template.
    const template = html`
      <span slot="title">${options.title}</span>
      <span slot="body" style="${options.bodyStyle}">${options.message}</span>
      <katapult-button slot="buttons" dialog-dismiss>Cancel</katapult-button>
      <div spacer slot="buttons"></div>
      <katapult-button icon="${options.button.icon}" slot="buttons" color="${options.button.color}" dialog-confirm
        >${options.button.text}</katapult-button
      >
    `;
    // Create Dialog.
    return DialogService.dialog({
      attributes: options.attributes,
      properties: options.properties,
      template
    }).promise;
  }

  static info(options = {}) {
    options.class ??= 'info';
    // Initialize options with default values.
    DialogService._initOptions(options);
    // Override template.
    const template = html`
      <span slot="title">${options.title}</span>
      <span slot="body" style="${options.bodyStyle}">${options.message}</span>
      <katapult-button icon="${options.button.icon}" slot="buttons" color="${options.button.color}" dialog-confirm
        >${options.button.text}</katapult-button
      >
    `;
    // Create Dialog.
    return DialogService.dialog({
      attributes: options.attributes,
      properties: options.properties,
      template
    }).promise;
  }

  static error(options = {}) {
    options.class ??= 'error';
    // Initialize options with default values.
    DialogService._initOptions(options);
    // Override template.
    const template = html`
      <span slot="title">${options.title}</span>
      <span slot="body" style="${options.bodyStyle}">${options.message}</span>
      <katapult-button icon="${options.button.icon}" slot="buttons" color="${options.button.color}" dialog-dismiss
        >${options.button.text}</katapult-button
      >
    `;
    // Create Dialog.
    return DialogService.dialog({
      attributes: options.attributes,
      template
    }).promise;
  }

  static _initOptions(options) {
    // Initialize Button.
    options.button ??= {};
    // Apply Class.
    switch (options.class) {
      case 'confirm':
        options.icon ??= 'help';
        options.title ??= 'Are you sure?';
        break;
      case 'delete':
        options.icon ??= 'delete';
        options.color ??= 'var(--paper-red-500)';
        options.title ??= 'Delete?';
        options.button.text ??= 'Delete';
        options.button.color ??= 'var(--paper-red-500)';
        options.message ??= 'Are you sure you want to delete this?';
      case 'error':
        options.icon ??= 'error';
        options.color ??= 'var(--paper-red-500)';
        options.title ??= 'Error';
        options.button.text ??= 'Dismiss';
        options.button.color ??= 'var(--paper-red-500)';
        options.message ??= `Error: ${options.err?.message ?? 'Unknown'}`;
    }
    // Apply defaults.
    options.color ??= 'var(--secondary-color)';
    options.button.color ??= 'var(--secondary-color)';
    options.button.text ??= 'Okay';
    options.button.icon ??= '';
    options.bodyStyle ??= 'white-space: pre-wrap;';
    // Initialize attributes.
    options.attributes ??= {};
    if (options.draggable) options.attributes.draggable = '';
    if (options.icon) options.attributes.icon = options.icon;
  }
}
